import { apiService } from './apiService';
import { DashboardMetrics, LiveData } from '../pages/LiveDashboard';

export interface ExportOptions {
  format: 'pdf' | 'excel' | 'csv' | 'json';
  timeframe: string;
  department: string;
  includeCharts: boolean;
  includeRawData: boolean;
  filename?: string;
}

export interface ExportData {
  metrics: DashboardMetrics;
  liveData: LiveData;
  exportDate: string;
  timeframe: string;
  department: string;
}

class ExportService {
  private generateFilename(options: ExportOptions): string {
    const timestamp = new Date().toISOString().split('T')[0];
    const dept = options.department === 'all' ? 'all-departments' : options.department;
    return `dashboard-${dept}-${options.timeframe}-${timestamp}.${options.format}`;
  }

  private async exportToExcel(data: ExportData, options: ExportOptions): Promise<void> {
    try {
      // For now, we'll use the backend export endpoint
      // In a full implementation, you could use libraries like xlsx.js for client-side export
      const response = await apiService.get('/dashboard/export', {
        params: {
          format: 'excel',
          timeframe: options.timeframe,
          department: options.department,
        },
        responseType: 'blob',
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', this.generateFilename(options));
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      throw new Error('Failed to export to Excel');
    }
  }

  private async exportToPDF(data: ExportData, options: ExportOptions): Promise<void> {
    try {
      // For now, we'll use the backend export endpoint
      const response = await apiService.get('/dashboard/export', {
        params: {
          format: 'pdf',
          timeframe: options.timeframe,
          department: options.department,
        },
        responseType: 'blob',
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', this.generateFilename(options));
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      throw new Error('Failed to export to PDF');
    }
  }

  private exportToCSV(data: ExportData, options: ExportOptions): void {
    try {
      // Convert data to CSV format
      const csvContent = this.convertToCSV(data);
      
      // Create download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', this.generateFilename(options));
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      throw new Error('Failed to export to CSV');
    }
  }

  private exportToJSON(data: ExportData, options: ExportOptions): void {
    try {
      // Convert data to JSON format
      const jsonContent = JSON.stringify(data, null, 2);
      
      // Create download link
      const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', this.generateFilename(options));
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting to JSON:', error);
      throw new Error('Failed to export to JSON');
    }
  }

  private convertToCSV(data: ExportData): string {
    const rows: string[] = [];
    
    // Add header
    rows.push('Dashboard Export Report');
    rows.push(`Export Date: ${data.exportDate}`);
    rows.push(`Timeframe: ${data.timeframe}`);
    rows.push(`Department: ${data.department}`);
    rows.push('');

    // First Response Time
    rows.push('First Response Time');
    rows.push('Metric,Value,Target,Trend,Percentage');
    rows.push(`Average Response Time,${data.metrics.firstResponseTime.average},${data.metrics.firstResponseTime.target},${data.metrics.firstResponseTime.trend},${data.metrics.firstResponseTime.percentage}%`);
    rows.push('');

    // Aging Tickets
    rows.push('Aging Tickets');
    rows.push('Priority,Count');
    rows.push(`Critical,${data.metrics.agingTickets.critical}`);
    rows.push(`High,${data.metrics.agingTickets.high}`);
    rows.push(`Medium,${data.metrics.agingTickets.medium}`);
    rows.push(`Low,${data.metrics.agingTickets.low}`);
    rows.push('');

    // Age Distribution
    rows.push('Age Distribution');
    rows.push('Age Group,Count,Percentage');
    data.metrics.agingTickets.distribution.forEach(item => {
      rows.push(`${item.age},${item.count},${item.percentage}%`);
    });
    rows.push('');

    // Resolution Rates
    rows.push('Resolution Rates');
    rows.push(`Overall Rate,${data.metrics.resolutionRates.overall}%`);
    rows.push('');

    rows.push('By Department');
    rows.push('Department,Rate,Count');
    data.metrics.resolutionRates.byDepartment.forEach(dept => {
      rows.push(`${dept.department},${dept.rate}%,${dept.count}`);
    });
    rows.push('');

    rows.push('By Category');
    rows.push('Category,Rate,Count');
    data.metrics.resolutionRates.byCategory.forEach(cat => {
      rows.push(`${cat.category},${cat.rate}%,${cat.count}`);
    });
    rows.push('');

    // Hotspots
    rows.push('Hotspots');
    rows.push(`Total Hotspots,${data.metrics.hotspots.total}`);
    rows.push(`Critical Hotspots,${data.metrics.hotspots.critical}`);
    rows.push('');

    rows.push('By Ward');
    rows.push('Ward,Count,Severity');
    data.metrics.hotspots.byWard.forEach(ward => {
      rows.push(`${ward.ward},${ward.count},${ward.severity}`);
    });
    rows.push('');

    rows.push('Recurrent Faults');
    rows.push('Location,Count,Last Occurrence');
    data.metrics.hotspots.recurrentFaults.forEach(fault => {
      rows.push(`${fault.location},${fault.count},${fault.lastOccurrence}`);
    });
    rows.push('');

    // Live Data
    rows.push('Live Data');
    rows.push(`Total Reports,${data.liveData.totalReports}`);
    rows.push(`Active Assignments,${data.liveData.activeAssignments}`);
    rows.push(`Online Technicians,${data.liveData.onlineTechnicians}`);
    rows.push(`Critical Issues,${data.liveData.criticalIssues}`);
    rows.push('');

    rows.push('Recent Activity');
    rows.push('Type,Title,Status,Priority,Location,Timestamp');
    data.liveData.recentActivity.forEach(activity => {
      rows.push(`${activity.type},${activity.title},${activity.status},${activity.priority},${activity.location},${activity.timestamp}`);
    });

    return rows.join('\n');
  }

  async exportDashboard(
    metrics: DashboardMetrics,
    liveData: LiveData,
    options: ExportOptions
  ): Promise<void> {
    const exportData: ExportData = {
      metrics,
      liveData,
      exportDate: new Date().toISOString(),
      timeframe: options.timeframe,
      department: options.department,
    };

    try {
      switch (options.format) {
        case 'excel':
          await this.exportToExcel(exportData, options);
          break;
        case 'pdf':
          await this.exportToPDF(exportData, options);
          break;
        case 'csv':
          this.exportToCSV(exportData, options);
          break;
        case 'json':
          this.exportToJSON(exportData, options);
          break;
        default:
          throw new Error(`Unsupported export format: ${options.format}`);
      }
    } catch (error) {
      console.error('Export failed:', error);
      throw error;
    }
  }

  // Generate a summary report for quick export
  generateSummaryReport(data: ExportData): string {
    const report = `
# Dashboard Summary Report

**Export Date:** ${data.exportDate}
**Timeframe:** ${data.timeframe}
**Department:** ${data.department}

## Key Performance Indicators

### First Response Time
- **Average:** ${data.metrics.firstResponseTime.average} minutes
- **Target:** ${data.metrics.firstResponseTime.target} minutes
- **Achievement:** ${data.metrics.firstResponseTime.percentage}%
- **Trend:** ${data.metrics.firstResponseTime.trend}

### Resolution Rates
- **Overall:** ${data.metrics.resolutionRates.overall}%
- **Top Department:** ${data.metrics.resolutionRates.byDepartment[0]?.department || 'N/A'} (${data.metrics.resolutionRates.byDepartment[0]?.rate || 0}%)

### Current Status
- **Total Reports:** ${data.liveData.totalReports}
- **Active Assignments:** ${data.liveData.activeAssignments}
- **Online Technicians:** ${data.liveData.onlineTechnicians}
- **Critical Issues:** ${data.liveData.criticalIssues}

### Hotspots
- **Total:** ${data.metrics.hotspots.total}
- **Critical:** ${data.metrics.hotspots.critical}
- **Top Ward:** ${data.metrics.hotspots.byWard[0]?.ward || 'N/A'} (${data.metrics.hotspots.byWard[0]?.count || 0} issues)

---
*Report generated automatically by Municipal Service Delivery Portal Dashboard*
    `;

    return report.trim();
  }

  // Export summary as markdown
  exportSummaryAsMarkdown(data: ExportData): void {
    const markdown = this.generateSummaryReport(data);
    const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `dashboard-summary-${data.timeframe}-${data.department}.md`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
}

export default new ExportService();





